"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/app/lib/utils";

const Dialog = DialogPrimitive.Root;

Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

interface DialogOverlayProps extends React.ComponentPropsWithoutRef<
   typeof DialogPrimitive.Overlay
> {
   overlayClassName?: string;
   blur?: boolean;
   [key: `data-${string}`]: string | undefined;
}

const DialogOverlay = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Overlay>,
   DialogOverlayProps
>(({ className, overlayClassName, blur = false, ...props }, ref) => (
   <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
         "fixed inset-0 z-50 bg-black/20 backdrop-blur-sm transition-all duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
         blur && "backdrop-blur-sm",
         overlayClassName,
         className
      )}
      {...props}
   />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<
   typeof DialogPrimitive.Content
> {
   overlayProps?: Partial<DialogOverlayProps>;
   preventOutsideClose?: boolean;
   hideCloseButton?: boolean;
}

const pulseKeyframes = `
  @keyframes borderHighlight {
    0% { border-color: #e2e8f0; }
    25% { border-color: #ef4444; }
    50% { border-color: #ef4444; box-shadow: 0 0 10px rgba(239, 68, 68, 0.5); }
    75% { border-color: #ef4444; }
    100% { border-color: #e2e8f0; }
  }
  
  .pulse-anim {
    animation: borderHighlight 0.8s ease forwards;
    border: 3px solid #ef4444 !important;
  }
`;

const DialogContent = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Content>,
   DialogContentProps
>(
   (
      {
         className,
         children,
         overlayProps,
         preventOutsideClose = false,
         hideCloseButton = false,
         ...props
      },
      ref
   ) => {
      const contentRef = React.useRef<HTMLDivElement>(null);

      const handleRef = React.useMemo(() => {
         return (node: HTMLDivElement) => {
            if (typeof ref === "function") {
               ref(node);
            } else if (ref) {
               ref.current = node;
            }
            contentRef.current = node;
         };
      }, [ref]);

      const handleOutsideClick = (e: any) => {
         if (preventOutsideClose && contentRef.current) {
            e.preventDefault();
            const element = contentRef.current;
            element.classList.remove("pulse-anim");
            void element.offsetWidth;
            element.classList.add("pulse-anim");
            setTimeout(() => {
               if (contentRef.current) {
                  contentRef.current.classList.remove("pulse-anim");
               }
            }, 300);
         }
      };

      const hasDesc = React.useMemo(() => {
         const search = (node: React.ReactNode): boolean => {
            if (React.isValidElement(node)) {
               if (
                  (node.type as any).displayName === "DialogDescription" ||
                  (node.type as any).displayName ===
                     DialogPrimitive.Description.displayName
               ) {
                  return true;
               }
               if (node.props && (node.props as any).children) {
                  return React.Children.toArray((node.props as any).children).some(
                     search
                  );
               }
            }
            return false;
         };
         return React.Children.toArray(children).some(search);
      }, [children]);

      const contentProps = { ...props };
      if (!hasDesc && props["aria-describedby"] === undefined) {
         contentProps["aria-describedby"] = undefined;
      }

      return (
         <DialogPortal>
            <style>{pulseKeyframes}</style>
            <DialogOverlay {...overlayProps} />
            <DialogPrimitive.Content
               ref={handleRef}
               // Shared dialog look — call sites pass layout classes only.
               // `border-none` kills dark-mode separation; `rounded-*` loses to `sm:rounded-*`.
               className={cn(
                  "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-2xl sm:rounded-2xl dialog-content",
                  className
               )}
               onPointerDownOutside={handleOutsideClick}
               {...contentProps}
            >
               {children}
               {!hideCloseButton && (
                  <DialogPrimitive.Close className="absolute right-4 top-4 rounded-xl opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                     <X className="h-4 w-4" />
                     <span className="sr-only">Close</span>
                  </DialogPrimitive.Close>
               )}
            </DialogPrimitive.Content>
         </DialogPortal>
      );
   }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
   <div
      className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
      {...props}
   />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
   <div
      className={cn(
         "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
         className
      )}
      {...props}
   />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Title>,
   React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
   <DialogPrimitive.Title
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
   />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Description>,
   React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
   <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
   />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
   Dialog,
   DialogPortal,
   DialogOverlay,
   DialogTrigger,
   DialogClose,
   DialogContent,
   DialogHeader,
   DialogFooter,
   DialogTitle,
   DialogDescription
};
